import { createSelector } from "reselect";
import {
  getImageUrlsArray,
  getModifiedLegacyPrices,
  getAddedLegacyPrices,
} from "halifax";
import {
  ITrackingProperties,
  SearchDistanceFromShopProperties,
  ViewedHotelDetailsProperties,
  HotelCfarQuote,
  HotelCancellationPolicyV2,
  HotelCfarQuoteAdditionalInfo,
  PassengerAncillaryPricing,
  AncillaryKindEnum,
  RoomInfoProductsWithTransformedIndexes,
  RoomProductType,
  RoomProduct,
  RoomProductWithTransformedIndexes,
  Cap1HotelCfarOfferFactsProperties,
  getLodgingTrackingProperties,
  CorpRoomProduct,
  RoomInfoProducts,
  CorpRoomInfoProducts,
  Place,
  LodgingCollectionEnum,
  Prices,
  CancellationPolicyScenarioEnum,
  Amount,
  DiscountAwarePrices,
  Cap1HotelCfarPremiumProperties,
} from "redmond";
import queryStringParser from "query-string";

import { localCache } from "@capone/common";
import { CfarBrandEnum, type CfarRateQuoteSuccess } from "@b2bportal/lodging-api";
import { IStoreState } from "../../../../reducers/types";
import { HotelShopCallState } from "../state";
import {
  getViewedHotelListProperties,
  getSearchedNightCount,
} from "../../../availability/reducer";
import {
  lodgingIdWithCfarQuotesSelector,
  isHotelCfarEnabledSelector,
  selectedCfarIdSelector,
  hasSelectedRefundableRoomSelector,
  DO_NOT_APPLY_OPTION_KEY,
  getHotelCfarQuoteByRoomIdSelector,
  getHotelCfarQuoteAdditionalInfoByRoomIdSelector,
  getHotelCfarQuoteCancellationByRoomIdSelector,
} from "../../../ancillary/reducer";
import { getSelectedAccount } from "../../../rewards/reducer";
import { getTravelWalletCredit } from "../../../travel-wallet/reducer";

export const getHotelShopSelectedLodging = (state: IStoreState) =>
  state.hotelShop.selectedLodging;

export const getHotelShopDateRange = (state: IStoreState) =>
  state.hotelShop.dateRange;

export const getCacheLodgingTokenResponse = (state: IStoreState) =>
  state.hotelShop.cacheLodgingTokenResponse;

export const getHotelShopSelectedLodgingId = createSelector(
  getHotelShopSelectedLodging,
  (selectedLodging) => selectedLodging?.lodging.id
);

export const getHotelShopImageUrlsArray = createSelector(
  [getHotelShopSelectedLodging],
  (availability) => getImageUrlsArray(availability?.lodging.media ?? [])
);

export const getHotelShopShopRequestId = createSelector(
  [getHotelShopSelectedLodging, getCacheLodgingTokenResponse],
  (availability, cacheLodgingTokenResponse) => {
    const { shopToken } = queryStringParser.parse(window.location.search);

    const localCacheShopRequest = localCache.get(shopToken as string);

    return cacheLodgingTokenResponse
      ? cacheLodgingTokenResponse.opaqueShopRequest
      : localCacheShopRequest || availability?.price?.opaqueShopRequest;
  }
);

export const getHotelShopRoomInfoProducts = (state: IStoreState) =>
  state.hotelShop.roomInfoProducts;

export const getHotelShopTrackingPropertiesV2 = (state: IStoreState) =>
  state.hotelShop.hotelShopTrackingPropertiesV2;

export const getHotelShopChosenRoomInfoIndex = (state: IStoreState) =>
  state.hotelShop.chosenRoomInfoIndex;

export const getHotelShopChosenProductIndex = (state: IStoreState) =>
  state.hotelShop.chosenProductIndex;

export const getHotelShopChosenRateIdSelector = (state: IStoreState) =>
  state.hotelShop.chosenRateId;

export const getSelectedRoomIdSelector = (state: IStoreState) =>
  state.hotelShop.selectedRoomId;

export const getHotelShopCancellationSummary = (state: IStoreState) =>
  state.hotelShop.cancellationSummary;

export const getHotelShopChosenRoomInfo = createSelector(
  [getHotelShopRoomInfoProducts, getHotelShopChosenRoomInfoIndex],
  (
    roomInfoProducts,
    chosenRoomInfoIndex
  ): RoomInfoProducts | CorpRoomInfoProducts | null => {
    if (chosenRoomInfoIndex !== null) {
      return roomInfoProducts[chosenRoomInfoIndex] ?? null;
    }

    return null;
  }
);

export const getHotelShopChosenProduct = createSelector(
  [
    getHotelShopRoomInfoProducts,
    getHotelShopChosenRoomInfoIndex,
    getHotelShopChosenProductIndex,
  ],
  (
    roomInfoProducts,
    chosenRoomInfoIndex,
    chosenProductIndex
  ): RoomProduct | CorpRoomProduct | null => {
    if (chosenRoomInfoIndex !== null && chosenProductIndex !== null) {
      return (
        roomInfoProducts[chosenRoomInfoIndex]?.products[chosenProductIndex] ??
        null
      );
    }

    return null;
  }
);

export const getHotelShopSelectedLodgingIsFavorited = (state: IStoreState) =>
  state.hotelShop.selectedLodgingIsFavorited;

export const getHotelShopChosenProductCorporateTravel = createSelector(
  [getHotelShopChosenProduct],
  (chosenProduct) => (chosenProduct as CorpRoomProduct)?.corporateTravel
);

export const getHotelShopCallState = (state: IStoreState) =>
  state.hotelShop.hotelShopCallState;

export const getHotelShopCallError = (state: IStoreState) =>
  state.hotelShop.hotelShopCallError;

export const getHotelShopPersistentLodgingTrackingProperties = (
  state: IStoreState
) => state.hotelShop.persistentLodgingTrackingProperties;

export const isMissingProductInfo = createSelector(
  getHotelShopCallState,
  getHotelShopRoomInfoProducts,
  (hotelShopCallState, roomInfoProducts) =>
    hotelShopCallState === HotelShopCallState.Success &&
    roomInfoProducts.length === 0
);

export const hasHotelShopFailed = createSelector(
  getHotelShopCallState,
  isMissingProductInfo,
  (hotelShopCallState, missingProductInfo) =>
    hotelShopCallState === HotelShopCallState.Failed || missingProductInfo
);

export const getViewedHotelDetailsTrackingProps = createSelector(
  getHotelShopChosenProduct,
  getHotelShopChosenRoomInfo,
  getHotelShopTrackingPropertiesV2,
  (
    product,
    roomInfo,
    hotelShopTrackingPropertiesV2
  ): ITrackingProperties | null =>
    product && roomInfo
      ? {
          properties: {
            ...product?.trackingPropertiesV2?.properties,
            ...roomInfo?.trackingPropertiesV2?.properties,
            ...hotelShopTrackingPropertiesV2?.properties,
          },
          encryptedProperties: [
            product?.trackingPropertiesV2?.encryptedProperties ?? "",
            roomInfo?.trackingPropertiesV2?.encryptedProperties ?? "",
            hotelShopTrackingPropertiesV2?.encryptedProperties ?? "",
          ],
        }
      : {
          properties: {
            ...hotelShopTrackingPropertiesV2?.properties,
          },
          encryptedProperties: [
            hotelShopTrackingPropertiesV2?.encryptedProperties ?? "",
          ],
        }
);

export const getHotelShopEntryPoint = (state: IStoreState) =>
  state.hotelShop.entryPoint;

export const getReceivedCrossSellTrackingProperties = (state: IStoreState) =>
  state.hotelShop.crossSellTrackingProperties;

export const getHotelDetailsEntrySource = (state: IStoreState) =>
  state.hotelShop.hotelDetailsEntrySource;

export const getHotelDetailsSortOrder = (state: IStoreState) =>
  state.hotelShop.sortOrder;

// In an effort to not have to re-write all the Hotel Shop logic with CFAR, we've just created a V2 of this selector.
// We'll use the new field `cfarQuotes` and filter the `RoomsList` options.
export const getHotelShopRoomInfoProductsWithTransformedIndexesV2 =
  createSelector(
    [
      getHotelShopSelectedLodging,
      getHotelShopRoomInfoProducts,
      getSearchedNightCount,
    ],
    (
      lodging,
      roomInfoProducts,
      nightCount
    ): RoomInfoProductsWithTransformedIndexes[] =>
      roomInfoProducts.map((roomInfoProduct, roomIndex) => ({
        ...roomInfoProduct,
        products: roomInfoProduct.products
          .flatMap((product, productIndex) => {
            const roomListOffers: CfarRateQuoteSuccess[] =
              product.cfarQuotes != null
                ? product.cfarQuotes.filter(
                    (quote): quote is CfarRateQuoteSuccess =>
                      quote.CfarRateQuote === "Success" &&
                      quote.merchandisingPlacement?.MerchandisingPlacement ===
                        "RoomsList"
                  )
                : [];
            const baseProduct = {
              roomIndex,
              productIndex,
              rateId: product.rateId,
            };

            // Copying a lot of the logic from the RoomTotalPriceAndRewardsWithCompliance as the two need to match
            const totalWithoutPropertyFee = product.feeBreakdown.total
              ? {
                  ...product.total,
                  fiat: {
                    ...product.total.fiat,
                    value:
                      product.total.fiat.value -
                      (product.taxBreakdown.salesTax?.fiat.value ?? 0) -
                      (product.taxBreakdown.cityTaxes?.fiat.value ?? 0),
                  },
                }
              : undefined;

            const isTotalPricesIncludingAllTaxes =
              !!lodging?.price?.displayTaxesAndFeesIncluded;

            const standardRefundPrices =
              totalWithoutPropertyFee ??
              product.tripTotalExcludingTaxes ??
              product.tripTotal;

            const baseRefundPrices =
              product.totalDiscountAware.priceWithUnmanagedDiscounts ||
              product.sellRate;

            return [
              // This is the default product
              {
                ...product,
                roomProductVariant: {
                  baseProduct,
                  variant: RoomProductType.Default,
                },
              } satisfies RoomProductWithTransformedIndexes,
              // But we may also have CFAR offers with a Room List placement
              ...roomListOffers.map((offer) => {
                const {
                  premiumAmount: premiumPrices,
                  coverageAmount,
                  coveragePercentage,
                  id: quoteId,
                  refundPremiumAtExercise: shouldRefundCfarPremium,
                  expirationInHotelTz: expirationDate,
                } = offer;

                const premiumPricesPerNight = getModifiedLegacyPrices(
                  premiumPrices as Prices, // Issue with the types from redmond and b2b-interfaces
                  (total: number) => Math.ceil(total / (nightCount ?? 1)) // Rounding here for consistency so we don't rely on the implementation of other modules
                );

                return {
                  ...product,
                  tripTotal: getAddedLegacyPrices(
                    product.tripTotal,
                    // Issue with the types from redmond and b2b-interfaces
                    premiumPrices as Prices
                  ),
                  total: getAddedLegacyPrices(
                    product.total,
                    // Issue with the types from redmond and b2b-interfaces
                    premiumPrices as Prices
                  ),
                  totalDiscountAware: {
                    ...product.totalDiscountAware,
                    priceWithUnmanagedDiscounts: getAddedLegacyPrices(
                      product.totalDiscountAware.priceWithUnmanagedDiscounts,
                      // Issue with the types from redmond and b2b-interfaces
                      premiumPrices as Prices
                    ),
                  },
                  sellRate: getAddedLegacyPrices(
                    product.sellRate,
                    // Issue with the types from redmond and b2b-interfaces
                    premiumPrices as Prices
                  ),
                  nightlyDiscountAware: product.nightlyDiscountAware
                    ? Object.keys(product.nightlyDiscountAware).reduce(
                        (acc, curr) => {
                          acc[curr] = getAddedLegacyPrices(
                            product.nightlyDiscountAware![curr],
                            premiumPricesPerNight
                          );
                          return acc;
                        },
                        {} as DiscountAwarePrices
                      )
                    : product.nightlyDiscountAware,
                  perNightSellRate: getAddedLegacyPrices(
                    product.perNightSellRate,
                    premiumPricesPerNight
                  ),
                  perNightSellRateTaxesAndFeesIncluded:
                    product.perNightSellRateTaxesAndFeesIncluded
                      ? getAddedLegacyPrices(
                          product.perNightSellRateTaxesAndFeesIncluded,
                          premiumPricesPerNight
                        )
                      : product.perNightSellRateTaxesAndFeesIncluded,
                  nightlyDiscountAwareTaxesAndFeesIncluded:
                    product.nightlyDiscountAwareTaxesAndFeesIncluded
                      ? Object.keys(
                          product.nightlyDiscountAwareTaxesAndFeesIncluded
                        ).reduce((acc, curr) => {
                          acc[curr] = getAddedLegacyPrices(
                            product.nightlyDiscountAwareTaxesAndFeesIncluded![
                              curr
                            ],
                            premiumPricesPerNight
                          );
                          return acc;
                        }, {} as DiscountAwarePrices)
                      : product.nightlyDiscountAwareTaxesAndFeesIncluded,
                  tripTotalExcludingTaxes: product.tripTotalExcludingTaxes
                    ? getAddedLegacyPrices(
                        product.tripTotalExcludingTaxes,
                        // Issue with the types from redmond and b2b-interfaces
                        premiumPrices as Prices
                      )
                    : product.tripTotalExcludingTaxes,
                  roomProductVariant: {
                    baseProduct,
                    // Issue with the types from redmond and b2b-interfaces
                    premiumPrices: premiumPrices as Prices,
                    premiumPricesPerNight,
                    quoteId: {
                      value: quoteId,
                    },
                    coveragePercentage,
                    expirationDate,
                    coverageAmount: {
                      amount: coverageAmount.fiat.value,
                      currency: coverageAmount.fiat.currencyCode,
                    } as Amount,
                    additionalInfo: {
                      shouldRefundCfarPremium,
                      standardRefundPrices: shouldRefundCfarPremium
                        ? getAddedLegacyPrices(
                            standardRefundPrices,
                            premiumPrices as Prices
                          )
                        : standardRefundPrices,
                      totalRefundIncludesTaxes: isTotalPricesIncludingAllTaxes,
                      baseRefundPrices: shouldRefundCfarPremium
                        ? getAddedLegacyPrices(
                            baseRefundPrices,
                            premiumPrices as Prices
                          )
                        : baseRefundPrices,
                      totalRefundPrices: shouldRefundCfarPremium
                        ? getAddedLegacyPrices(
                            product.total,
                            premiumPrices as Prices
                          )
                        : product.total,
                      isRefundableByCapitalOne: true,
                    },
                    cancellationPolicy: null,
                    pricingDescription:
                      offer.trackingProperties.properties
                        .cfar_quote_pricing_description,
                    cancellationPolicyScenario: {
                      type: CancellationPolicyScenarioEnum.Other,
                    },
                    variant: RoomProductType.RefundableRoom,
                  },
                } satisfies RoomProductWithTransformedIndexes;
              }),
            ];
          })
          .sort((a, b) => a.sellRate.fiat.value - b.sellRate.fiat.value),
      }))
  );

export const getCheapestProduct = createSelector(
  [getHotelShopRoomInfoProductsWithTransformedIndexesV2],
  (hotelShopRoomInfoProductsWithTransformedIndexesV2) =>
    hotelShopRoomInfoProductsWithTransformedIndexesV2[0]?.products[0]
);

export const getHotelShopRoomInfoProductsWithTransformedIndexes =
  createSelector(
    getHotelShopRoomInfoProductsWithTransformedIndexesV2,
    (
      hotelShopRoomInfoProductsWithTransformedIndexesV2
    ): RoomInfoProductsWithTransformedIndexes[] =>
      hotelShopRoomInfoProductsWithTransformedIndexesV2
  );


// Note that we can only have at most one Refundable Room product for a partciular Room
// due to the filtering logic performed in getHotelShopRoomInfoProductsWithTransformedIndexes
export const getHotelShopRoomInfoProductsRefundableRoomProductCount =
  createSelector(
    getHotelShopRoomInfoProductsWithTransformedIndexes,
    (roomInfoProductsWithTransformedIndexes) =>
      roomInfoProductsWithTransformedIndexes.filter(
        (roomInfoProduct) =>
          roomInfoProduct.products.find(
            (product) =>
              product.roomProductVariant?.variant ===
              RoomProductType.RefundableRoom
          ) !== undefined
      ).length
  );

const getHotelShopRoomInfoRoomsImagesCount = createSelector(
  getHotelShopRoomInfoProducts,
  (
    roomInfoProducts
  ): {
    rooms_with_images_count: number;
    rooms_without_images_count: number;
  } => {
    let rooms_with_images_cnt = 0;
    let rooms_without_images_cnt = 0;
    for (const roomInfoProduct of roomInfoProducts) {
      rooms_with_images_cnt += roomInfoProduct.roomInfo.media.length ? 1 : 0;
      rooms_without_images_cnt += roomInfoProduct.roomInfo.media.length ? 0 : 1;
    }
    return {
      rooms_with_images_count: rooms_with_images_cnt,
      rooms_without_images_count: rooms_without_images_cnt,
    };
  }
);

export const getViewedHotelDetailsProperties = createSelector(
  getViewedHotelListProperties,
  getSelectedAccount,
  getHotelShopSelectedLodging,
  getViewedHotelDetailsTrackingProps,
  getHotelShopRoomInfoProducts,
  getHotelShopEntryPoint,
  getHotelShopRoomInfoProductsRefundableRoomProductCount,
  getHotelShopRoomInfoRoomsImagesCount,
  getHotelDetailsEntrySource,
  getHotelDetailsSortOrder,
  getHotelShopSelectedLodgingIsFavorited,
  getHotelShopPersistentLodgingTrackingProperties,
  (
    viewedHotelListProperties,
    account,
    availability,
    trackingProps,
    roomInfoProducts,
    hotelShopEntryPoints,
    refundableRoomProductCount,
    roomInfoRoomsImagesCount,
    hotelDetailsEntrySource,
    sortOrder,
    selectedLodgingIsFavorited,
    persistentLodgingTrackingProperties
  ): ITrackingProperties<ViewedHotelDetailsProperties> => {
    const {
      lodgingSelection,
      selectedLodgingIndex,
    }: { lodgingSelection?: string; selectedLodgingIndex?: number } =
      queryStringParser.parse(location.search);

    let parsedLodgingSelection: Place | undefined;

    try {
      if (lodgingSelection) {
        parsedLodgingSelection = JSON.parse(
          decodeURIComponent(lodgingSelection)
        );
      }
    } catch (error) {
      console.error(error);
    }

    return {
      properties: {
        ...getLodgingTrackingProperties(availability).properties,
        ...viewedHotelListProperties.properties,
        account_type_selected: account?.productDisplayName || "",
        rooms_shown: roomInfoProducts.length || 0,
        account_use_type: account?.accountUseType,
        account_allow_rewards_redemption: account?.allowRewardsRedemption,
        entry_type: hotelShopEntryPoints || undefined,
        room_media_count: availability?.lodging.media.length || 0,
        cfar_rr_offer: refundableRoomProductCount,
        rooms_with_images_count:
          roomInfoRoomsImagesCount.rooms_with_images_count,
        rooms_without_images_count:
          roomInfoRoomsImagesCount.rooms_without_images_count,
        google_place_id: parsedLodgingSelection?.placeId,
        lodging_row_index: selectedLodgingIndex
          ? Number(selectedLodgingIndex)
          : undefined,
        ...trackingProps?.properties,
        hotel_details_entry_source: hotelDetailsEntrySource || undefined,
        product_type: [
          LodgingCollectionEnum.Premier,
          LodgingCollectionEnum.Lifestyle,
        ].includes(
          availability?.lodgingCollection || LodgingCollectionEnum.NoCollection
        )
          ? (availability?.lodgingCollection as string)
          : "",
        sort_order: sortOrder,
        free_cancellation_shown: availability?.isFreeCancel,
        ...(selectedLodgingIsFavorited !== undefined && {
          is_favorited: selectedLodgingIsFavorited,
        }),
        ...(persistentLodgingTrackingProperties ?? {}),
      },
      encryptedProperties: [
        ...getLodgingTrackingProperties(availability).encryptedProperties,
        ...viewedHotelListProperties.encryptedProperties,
        availability?.trackingPropertiesV2?.encryptedProperties ?? "",
        availability?.bestOfferThisLodging?.trackingPropertiesV2
          ?.encryptedProperties ?? "",
        ...(trackingProps?.encryptedProperties ?? []),
      ],
    };
  }
);

export const hotelShopProgressSelector = (state: IStoreState) =>
  state.hotelShop.progress;

export const getNearbyLodgings = (state: IStoreState) =>
  state.hotelShop.nearbyLodgings;

export const getNearbyLodgingsAvailabilityCallState = (state: IStoreState) =>
  state.hotelShop.nearbyLodgingsAvailabilityCallState;

export const getSimilarHotels = (state: IStoreState) =>
  state.hotelShop.similarHotels;

export const getSimilarHotelsAvailabilityCallState = (state: IStoreState) =>
  state.hotelShop.similarHotelsAvailabilityCallState;

export const getSimilarHotelsAvailabilityTrackingProperties = (
  state: IStoreState
) => state.hotelShop.similarHotelsAvailabilityTrackingProperties;

export const getDistanceFromLocation = (state: IStoreState) =>
  state.hotelShop.distanceFromLocation;

export const getDistanceFromLocationCategories = (state: IStoreState) =>
  state.hotelShop.distanceFromLocationCategories;

export const getDistanceFromLocationCategoriesLoading = (state: IStoreState) =>
  state.hotelShop.distanceFromLocationCategoriesLoading;

export const getSearchDistanceFromShopProperties = createSelector(
  getViewedHotelDetailsProperties,
  getDistanceFromLocation,
  (
    viewedHotelDetailsProperties,
    distanceFromLocation
  ): ITrackingProperties<SearchDistanceFromShopProperties> => ({
    properties: {
      point_of_interest: distanceFromLocation?.label || "",
      ...viewedHotelDetailsProperties.properties,
    },
    encryptedProperties: [...viewedHotelDetailsProperties.encryptedProperties],
  })
);

export const getHotelShopRecommendedBasedOnPreferences = (state: IStoreState) =>
  state.hotelShop.recommendedBasedOnPreferences;

// Travel Wallet
export const showOfferBasedOnSelectedLodgingSelector = createSelector(
  getHotelShopSelectedLodging,
  getTravelWalletCredit,
  (selectedLodging, credit): boolean =>
    !!selectedLodging?.bestOfferThisLodging &&
    ((!!credit &&
      Math.abs(credit.amount.amount) ===
        Math.abs(selectedLodging.bestOfferThisLodging.amount.amount)) ||
      !!selectedLodging.bestOfferThisLodging.availabilityPageBanner)
);

const areCfarIndexesInvalid = ({
  hotelShopChosenRoomInfoIndex,
  hotelShopChosenProductIndex,
  hotelShopSelectedLodgingId,
  lodgingIdWithCfarQuotes,
}: {
  hotelShopChosenRoomInfoIndex: number | null;
  hotelShopChosenProductIndex: number | null;
  hotelShopSelectedLodgingId: string | undefined;
  lodgingIdWithCfarQuotes: string | null;
}) =>
  hotelShopChosenRoomInfoIndex === null ||
  hotelShopChosenProductIndex === null ||
  !hotelShopSelectedLodgingId ||
  !lodgingIdWithCfarQuotes ||
  hotelShopSelectedLodgingId !== lodgingIdWithCfarQuotes;

// CFAR

// @FLEX-TODO: Once rolled out, we need to remove the old selector
// Returns the CFAR quotes for the selected lodging, this could include multiple quotes: RoomList and Standalone
export const hotelCfarQuotesFromChosenRoomProductV2Selector = createSelector(
  [getHotelShopChosenProduct],
  (hotelShopChosenProduct) =>
    hotelShopChosenProduct?.cfarQuotes?.filter(
      (quote): quote is CfarRateQuoteSuccess =>
        quote != null && quote.CfarRateQuote === "Success"
    ) ?? []
);

// @FLEX-TODO: Once rolled out, we need to remove the old selector
export const hotelCfarQuoteFromChosenRoomProductV2Selector = createSelector(
  selectedCfarIdSelector,
  hotelCfarQuotesFromChosenRoomProductV2Selector,
  (selectedCfarId, hotelCfarQuotesFromChosenRoomProduct) =>
    hotelCfarQuotesFromChosenRoomProduct.find(
      (quote) => quote.id === selectedCfarId?.value
    )
);

export const selectRoomsListCfarQuoteFromChosenRoomProduct = createSelector(
  [hotelCfarQuotesFromChosenRoomProductV2Selector],
  (hotelCfarQuotesFromChosenRoomProduct) =>
    hotelCfarQuotesFromChosenRoomProduct.find(
      (quote) =>
        quote.merchandisingPlacement?.MerchandisingPlacement === "RoomsList"
    )
);

export const selectStandaloneCfarQuoteFromChosenRoomProduct = createSelector(
  [hotelCfarQuotesFromChosenRoomProductV2Selector],
  (hotelCfarQuotesFromChosenRoomProduct) =>
    hotelCfarQuotesFromChosenRoomProduct.find(
      (quote) =>
        quote.merchandisingPlacement?.MerchandisingPlacement === "Standalone"
    )
);


export const getHotelCfarPremiumProperties = createSelector(
  [getHotelShopChosenRoomInfo, selectStandaloneCfarQuoteFromChosenRoomProduct],
  (chosenRoomInfo, standaloneCfarQuote): Cap1HotelCfarPremiumProperties | object => {
    let hotelCfarPremiumProperties = {};
    if (standaloneCfarQuote) {
      hotelCfarPremiumProperties = {
        cfar_standalone_premium_percentage: standaloneCfarQuote.trackingProperties?.properties?.cfar_quote_premium_percentage,
        cfar_standalone_premium_usd: standaloneCfarQuote.trackingProperties?.properties?.cfar_quote_premium_amount_usd,
      };
    }
    if (chosenRoomInfo == null || chosenRoomInfo.products.length === 0) {
      return hotelCfarPremiumProperties;
    }
    const chosenProduct = chosenRoomInfo.products?.[chosenRoomInfo.products.length - 1];
    const cfarQuotes = chosenProduct?.cfarQuotes;
    if (!cfarQuotes) {
      return hotelCfarPremiumProperties;
    }

    /* eslint-disable camelcase */
    return cfarQuotes.reduce((properties, quote) => {
      const { CfarRateQuote, trackingProperties } = quote;

      if (CfarRateQuote === 'Success' && quote.brand?.CfarBrand) {
        const { cfar_quote_premium_percentage, cfar_quote_premium_amount_usd } = trackingProperties?.properties || {};

        switch (quote.brand.CfarBrand) {
          case CfarBrandEnum.SyntheticNegative:
            return {
              ...properties,
              cfar_rr_premium_percentage: cfar_quote_premium_percentage,
              cfar_rr_premium_usd: cfar_quote_premium_amount_usd,
            };
          default:
            return properties;
        }
      }

      return properties;
    }, hotelCfarPremiumProperties);
  }
);

export const hotelCfarQuoteFromChosenRoomProductSelector = createSelector(
  getHotelShopSelectedLodgingId,
  lodgingIdWithCfarQuotesSelector,
  getHotelShopChosenRoomInfoIndex,
  getHotelShopChosenProductIndex,
  getSelectedRoomIdSelector,
  getHotelCfarQuoteByRoomIdSelector,
  getHotelShopChosenRateIdSelector,
  (
    hotelShopSelectedLodgingId,
    lodgingIdWithCfarQuotes,
    hotelShopChosenRoomInfoIndex,
    hotelShopChosenProductIndex,
    selectedRoomId,
    hotelCfarQuoteByRoomId,
    chosenHotelShopRateId
  ): HotelCfarQuote | null => {
    if (
      areCfarIndexesInvalid({
        hotelShopChosenRoomInfoIndex,
        hotelShopChosenProductIndex,
        hotelShopSelectedLodgingId,
        lodgingIdWithCfarQuotes,
      })
    ) {
      return null;
    }

    return hotelCfarQuoteByRoomId({
      selectedRoomId,
      selectedRateId: chosenHotelShopRateId,
    });
  }
);

export const cancellationPolicyFromChosenRoomProductV2Selector = createSelector(
  [getHotelShopChosenProduct],
  (hotelShopChosenProduct) => hotelShopChosenProduct?.cancellationPolicyV2
);

export const cancellationPolicyFromChosenRoomProductSelector = createSelector(
  getHotelShopSelectedLodgingId,
  lodgingIdWithCfarQuotesSelector,
  getHotelShopChosenRoomInfoIndex,
  getHotelShopChosenProductIndex,
  getHotelCfarQuoteCancellationByRoomIdSelector,
  getSelectedRoomIdSelector,
  getHotelShopChosenRateIdSelector,
  (
    hotelShopSelectedLodgingId,
    lodgingIdWithCfarQuotes,
    hotelShopChosenRoomInfoIndex,
    hotelShopChosenProductIndex,
    getHotelCfarQuoteCancellationByRoomId,
    selectedRoomId,
    chosenRateId
  ): HotelCancellationPolicyV2 | null => {
    if (
      areCfarIndexesInvalid({
        hotelShopChosenRoomInfoIndex,
        hotelShopChosenProductIndex,
        hotelShopSelectedLodgingId,
        lodgingIdWithCfarQuotes,
      })
    ) {
      return null;
    }

    return getHotelCfarQuoteCancellationByRoomId({
      selectedRoomId,
      selectedRateId: chosenRateId,
    });
  }
);

export const additionalInfoFromChosenRoomProductSelector = createSelector(
  getHotelShopSelectedLodgingId,
  lodgingIdWithCfarQuotesSelector,
  getHotelShopChosenRoomInfoIndex,
  getHotelShopChosenProductIndex,
  getHotelCfarQuoteAdditionalInfoByRoomIdSelector,
  getSelectedRoomIdSelector,
  getHotelShopChosenRateIdSelector,
  (
    hotelShopSelectedLodgingId,
    lodgingIdWithCfarQuotes,
    hotelShopChosenRoomInfoIndex,
    hotelShopChosenProductIndex,
    getHotelCfarQuoteAdditionalInfoByRoomId,
    selectedRoomId,
    chosenHotelShopRateId
  ): HotelCfarQuoteAdditionalInfo | null => {
    if (
      areCfarIndexesInvalid({
        hotelShopChosenRoomInfoIndex,
        hotelShopChosenProductIndex,
        hotelShopSelectedLodgingId,
        lodgingIdWithCfarQuotes,
      })
    ) {
      return null;
    }

    return getHotelCfarQuoteAdditionalInfoByRoomId({
      selectedRoomId,
      selectedRateId: chosenHotelShopRateId,
    });
  }
);

// @FLEX-TODO: Once rolled out, we need to remove the old selector
export const getAddedAncillariesPricingV2 = createSelector(
  hotelCfarQuoteFromChosenRoomProductV2Selector,
  (
    hotelCfarQuoteFromChosenRoomProductSelectorV2
  ): PassengerAncillaryPricing[] => {
    const ancillaryPricing: PassengerAncillaryPricing[] = [];
    if (hotelCfarQuoteFromChosenRoomProductSelectorV2) {
      ancillaryPricing.push({
        kind: AncillaryKindEnum.Cfar,
        premium:
          hotelCfarQuoteFromChosenRoomProductSelectorV2.premiumAmount as Prices,
      });
    }
    return ancillaryPricing;
  }
);
export const getAddedAncillariesPricing = createSelector(
  selectedCfarIdSelector,
  hotelCfarQuoteFromChosenRoomProductSelector,
  (
    selectedCfarId,
    hotelCfarQuoteFromChosenRoomProduct
  ): PassengerAncillaryPricing[] => {
    const ancillaryPricing: PassengerAncillaryPricing[] = [];
    if (
      !!selectedCfarId &&
      !!hotelCfarQuoteFromChosenRoomProduct &&
      selectedCfarId.value === hotelCfarQuoteFromChosenRoomProduct.id.value
    ) {
      ancillaryPricing.push({
        kind: AncillaryKindEnum.Cfar,
        premium: hotelCfarQuoteFromChosenRoomProduct.premiumPrices,
      });
    }
    return ancillaryPricing;
  }
);

export const isCfarAvailableInChosenRoomProductSelector = createSelector(
  hasSelectedRefundableRoomSelector,
  hotelCfarQuotesFromChosenRoomProductV2Selector,
  (
    hasSelectedRefundableRoom,
    hotelCfarQuotesFromChosenRoomProductV2
  ): boolean =>
    hotelCfarQuotesFromChosenRoomProductV2.some(
      (quote) =>
        quote.merchandisingPlacement?.MerchandisingPlacement === "Standalone"
    ) && !hasSelectedRefundableRoom
);

export const hasSelectedCfarOptionSelector = createSelector(
  selectedCfarIdSelector,
  isCfarAvailableInChosenRoomProductSelector,
  (selectedCfarId, isCfarAvailable): boolean =>
    !isCfarAvailable || !!selectedCfarId
);

export const isCfarOptionSelectionCompleteSelector = createSelector(
  isCfarAvailableInChosenRoomProductSelector,
  hasSelectedCfarOptionSelector,
  isHotelCfarEnabledSelector,
  (isCfarAvailable, hasSelectedCfarOption, isCfarEnabled): boolean =>
    !isCfarEnabled || !isCfarAvailable || hasSelectedCfarOption
);

export const isAddOnOptionAvailableSelector = createSelector(
  isCfarAvailableInChosenRoomProductSelector,
  (isCfarAvailable): boolean => isCfarAvailable
);

export const isOptionSelectionCompleteSelector = createSelector(
  isCfarOptionSelectionCompleteSelector,
  (isCfarOptionSelectionComplete): boolean => isCfarOptionSelectionComplete
);

export const getRefundableRoomProductWithSelectedCfarId = createSelector(
  getHotelShopRoomInfoProductsWithTransformedIndexes,
  selectedCfarIdSelector,
  (
    roomInfoProductsWithTransformedIndexes,
    selectedCfarId
  ): RoomProductWithTransformedIndexes | undefined => {
    if (selectedCfarId && selectedCfarId.value !== DO_NOT_APPLY_OPTION_KEY) {
      let refundableRoomProduct: RoomProductWithTransformedIndexes | undefined;

      roomInfoProductsWithTransformedIndexes.find(
        (roomInfoProducts) =>
          !!roomInfoProducts.products.find((roomProduct) => {
            if (
              !!roomProduct?.roomProductVariant &&
              roomProduct.roomProductVariant.variant ===
                RoomProductType.RefundableRoom &&
              roomProduct.roomProductVariant.quoteId.value ===
                selectedCfarId.value
            ) {
              refundableRoomProduct = roomProduct;
              return true;
            }
            return false;
          })
      );

      return refundableRoomProduct;
    }

    return undefined;
  }
);

// Note that we can only have at most one Refundable Room product for a partciular Room
// due to the filtering logic performed in getHotelShopRoomInfoProductsWithTransformedIndexes
export const getRefundableRoomProductCorrespondingToChosenRoom = createSelector(
  getHotelShopChosenRoomInfoIndex,
  getHotelShopRoomInfoProductsWithTransformedIndexes,
  (chosenRoomInfoIndex, roomInfoProductsWithTransformedIndexes) => {
    if (
      chosenRoomInfoIndex !== null &&
      roomInfoProductsWithTransformedIndexes[chosenRoomInfoIndex]
    ) {
      return roomInfoProductsWithTransformedIndexes[
        chosenRoomInfoIndex
      ].products.find(
        (product) =>
          product.roomProductVariant?.variant === RoomProductType.RefundableRoom
      );
    }

    return undefined;
  }
);

export const getHotelCfarOfferChoicePropertiesSelector = createSelector(
  selectedCfarIdSelector,
  selectStandaloneCfarQuoteFromChosenRoomProduct,
  selectRoomsListCfarQuoteFromChosenRoomProduct,
  hasSelectedRefundableRoomSelector,
  (
      selectedCfarId,
      standaloneCfarQuoteFromChosenRoomProduct,
      roomsListCfarQuoteFromChosenRoomProduct,
      hasSelectedRefundableRoom
    ): ((
      context: "shop" | "customize" | "book"
    ) => Pick<
      Cap1HotelCfarOfferFactsProperties,
      "cfar_rr_hotel_choice" | "cfar_choice"
    >) =>
    (context: "shop" | "customize" | "book") => {
      // note: it cannot be replaced by hotelCfarQuoteFromChosenRoomProductSelector, because the refundable room can be filtered out
      const hasSelectedCfar =
        !!selectedCfarId && selectedCfarId.value !== DO_NOT_APPLY_OPTION_KEY;

      const hasRefundableRoomOption =
        roomsListCfarQuoteFromChosenRoomProduct != null;

      const refundableRoomChoice = hasRefundableRoomOption
        ? hasSelectedRefundableRoom
          ? 1
          : 0
        : "none";

      const hasHotelCfarQuoteOption =
        standaloneCfarQuoteFromChosenRoomProduct != null;

      const cfarChoice = hasHotelCfarQuoteOption
        ? hasSelectedCfar
          ? 1
          : 0
        : "none";

      switch (context) {
        case "shop": {
          /*
            cfar_choice:
              0 = hotel rate selected
              1 = c1 refundable room selected 
              none = no c1 refundable room offered

            cfar_rr_hotel_choice
              0 = hotel rate selected
              1 = c1 refundable room selected
              none = no c1 refundable room offered
          */
          return {
            cfar_choice: refundableRoomChoice,
            cfar_rr_hotel_choice: refundableRoomChoice,
          };
        }
        case "customize": {
          /*
            cfar_choice
              0 = hotel rate selected
              1 = c1 refundable room selected OR standalone cfar attached
              none = no c1 refundable room/standalone cfar offered
          */
          return {
            cfar_choice: cfarChoice,
          };
        }
        case "book": {
          /*
            cfar_choice
              0 = hotel rate selected
              1 = c1 refundable room selected OR standalone cfar attached
              none = no c1 refundable room/ no standalone cfar offered

            cfar_rr_hotel_choice
              0 = hotel rate selected
              1 = c1 refundable room 
              none = no c1 refundable room offered
          */
          return {
            cfar_choice: cfarChoice,
            cfar_rr_hotel_choice: refundableRoomChoice,
          };
        }
        default:
          return {
            cfar_choice: "none",
            cfar_rr_hotel_choice: "none",
          };
      }
    }
);

// Corporate Travel

export const getHotelShopChosenProductHotelLoyaltyProgramCode = createSelector(
  [getHotelShopChosenProduct],
  (product) =>
    product && "loyaltyProgramCode" in product
      ? product.loyaltyProgramCode ?? ""
      : ""
);

export const getUserPassengers = (state: IStoreState) =>
  state.hotelBook.userPassengers;

export const getUserSelectedTravelerId = (state: IStoreState) =>
  state.hotelBook.userSelectedTravelerId;

// TODO(remove): legacy loyalty number fetcher for pre multiroom amadeus epic
export const getLoyaltyNumberForChosenProduct = createSelector(
  [
    getHotelShopChosenProductHotelLoyaltyProgramCode,
    getUserPassengers,
    getUserSelectedTravelerId,
  ],
  (loyaltyProgram, passengers, selectedTravelerId) => {
    if (!loyaltyProgram || !selectedTravelerId) {
      return null;
    }

    const traveler = passengers?.find(
      (passenger) => passenger.id === selectedTravelerId
    );

    return traveler?.hotelLoyalty[loyaltyProgram]?.value ?? null;
  }
);

export const getHotelShopLodgingSelection = (state: IStoreState) =>
  state.hotelShop.hotelShopLodgingSelection;
